export const GAMEPLAY_ART_SPEC = Object.freeze({
  artboard: Object.freeze({ width: 941, height: 1672 }),

  hiddenBolts: Object.freeze([
    Object.freeze({
      id: 'green-4',
      revealAfterBoltId: 'red-3',
      x: 351,
      y: 849,
      releasePieceId: 'parity-red-low-left',
    }),
    Object.freeze({
      id: 'gold-4',
      revealAfterBoltId: 'red-4',
      x: 585,
      y: 849,
      releasePieceId: 'parity-red-low-right',
    }),
  ]),

  movedBolts: Object.freeze([
    Object.freeze({ id: 'blue-2', x: 336, y: 949 }),
    Object.freeze({ id: 'blue-4', x: 607, y: 949 }),
  ]),

  holes: Object.freeze([
    Object.freeze({ id: 'hole-top-left', x: 330, y: 264, size: 62, depth: 4 }),
    Object.freeze({ id: 'hole-top-right', x: 601, y: 264, size: 62, depth: 4 }),
    Object.freeze({ id: 'hole-support-left', x: 330, y: 326, size: 57, depth: 7 }),
    Object.freeze({ id: 'hole-support-right', x: 601, y: 326, size: 57, depth: 7 }),
    Object.freeze({ id: 'hole-blue-left', x: 354, y: 581, size: 63, depth: 10, followObjectId: 'bar-blue-left' }),
    Object.freeze({ id: 'hole-lower-left', x: 207, y: 852, size: 61, depth: 6 }),
    Object.freeze({ id: 'hole-green-lower', x: 471, y: 851, size: 59, depth: 14, followObjectId: 'bar-green-vertical' }),
    Object.freeze({ id: 'hole-lower-right', x: 735, y: 852, size: 61, depth: 6 }),
  ]),

  lowerRedPieces: Object.freeze([
    Object.freeze({
      id: 'parity-red-low-left',
      x: 257,
      y: 808,
      w: 164,
      h: 79,
      depth: 13,
      releaseAfterBoltId: 'red-3',
    }),
    Object.freeze({
      id: 'parity-red-low-right',
      x: 520,
      y: 808,
      w: 164,
      h: 79,
      depth: 13,
      releaseAfterBoltId: 'red-4',
    }),
  ]),
});

function pct(value, total) {
  return `${(value / total) * 100}%`;
}

function place(node, x, y) {
  node.style.left = pct(x, GAMEPLAY_ART_SPEC.artboard.width);
  node.style.top = pct(y, GAMEPLAY_ART_SPEC.artboard.height);
}

function createHole(spec) {
  const hole = document.createElement('span');
  hole.className = 'bf-hole bf-parity-hole';
  hole.setAttribute('aria-hidden', 'true');
  hole.dataset.parityHoleId = spec.id;
  if (spec.followObjectId) hole.dataset.followObjectId = spec.followObjectId;
  place(hole, spec.x, spec.y);
  hole.style.width = pct(spec.size, GAMEPLAY_ART_SPEC.artboard.width);
  hole.style.zIndex = String(spec.depth);
  return hole;
}

function createLowerRedPiece(spec) {
  const piece = document.createElement('div');
  piece.className = 'bf-piece bf-metal--red bf-parity-red-piece';
  piece.setAttribute('aria-hidden', 'true');
  piece.dataset.parityPieceId = spec.id;
  piece.dataset.releaseAfterBoltId = spec.releaseAfterBoltId;
  piece.style.left = pct(spec.x, GAMEPLAY_ART_SPEC.artboard.width);
  piece.style.top = pct(spec.y, GAMEPLAY_ART_SPEC.artboard.height);
  piece.style.width = pct(spec.w, GAMEPLAY_ART_SPEC.artboard.width);
  piece.style.height = pct(spec.h, GAMEPLAY_ART_SPEC.artboard.height);
  piece.style.zIndex = String(spec.depth);
  return piece;
}

function revealBolt(stage, boltSpec) {
  const bolt = stage.querySelector(`[data-bolt-id="${boltSpec.id}"]`);
  if (!bolt) return;
  bolt.classList.remove('is-parity-concealed', 'is-removed');
  bolt.disabled = false;
  bolt.removeAttribute('aria-hidden');
  bolt.classList.add('is-parity-revealed');
}

function releaseParityPiece(stage, pieceId) {
  stage.querySelector(`[data-parity-piece-id="${pieceId}"]`)?.classList.add('is-released');
}

function watchBolt(stage, boltId, callback) {
  const bolt = stage.querySelector(`[data-bolt-id="${boltId}"]`);
  if (!bolt) return null;

  const runIfRemoved = () => {
    if (bolt.classList.contains('is-removed')) callback();
  };

  const observer = new MutationObserver(runIfRemoved);
  observer.observe(bolt, { attributes: true, attributeFilter: ['class'] });
  runIfRemoved();
  return observer;
}

function watchPiece(stage, objectId) {
  const piece = stage.querySelector(`[data-object-id="${objectId}"]`);
  if (!piece) return null;

  const update = () => {
    if (!piece.classList.contains('is-released')) return;
    stage
      .querySelectorAll(`[data-follow-object-id="${objectId}"]`)
      .forEach((hole) => hole.classList.add('is-released'));
  };

  const observer = new MutationObserver(update);
  observer.observe(piece, { attributes: true, attributeFilter: ['class'] });
  update();
  return observer;
}

export function applyGameplayArtSpec(stage) {
  if (!stage || stage.dataset.parityPass1Applied === 'true') return () => {};
  stage.dataset.parityPass1Applied = 'true';

  GAMEPLAY_ART_SPEC.movedBolts.forEach((spec) => {
    const bolt = stage.querySelector(`[data-bolt-id="${spec.id}"]`);
    if (bolt) place(bolt, spec.x, spec.y);
  });

  GAMEPLAY_ART_SPEC.hiddenBolts.forEach((spec) => {
    const bolt = stage.querySelector(`[data-bolt-id="${spec.id}"]`);
    if (!bolt) return;
    place(bolt, spec.x, spec.y);
    bolt.classList.add('is-parity-concealed', 'is-removed');
    bolt.disabled = true;
    bolt.setAttribute('aria-hidden', 'true');
  });

  stage.querySelectorAll('.bf-bolt--anchor').forEach((bolt) => {
    if (bolt.querySelector('.bf-bolt__pad')) return;
    const pad = document.createElement('span');
    pad.className = 'bf-bolt__pad';
    pad.setAttribute('aria-hidden', 'true');
    bolt.prepend(pad);
  });

  const originalLowerRed = stage.querySelector('[data-object-id="bar-red-low"]');
  if (originalLowerRed) originalLowerRed.classList.add('bf-parity-replaced');

  GAMEPLAY_ART_SPEC.lowerRedPieces.forEach((spec) => {
    stage.querySelector('.bf-board-shell')?.append(createLowerRedPiece(spec));
  });

  GAMEPLAY_ART_SPEC.holes.forEach((spec) => {
    stage.querySelector('.bf-board-shell')?.append(createHole(spec));
  });

  const observers = [];

  GAMEPLAY_ART_SPEC.hiddenBolts.forEach((spec) => {
    const observer = watchBolt(stage, spec.revealAfterBoltId, () => {
      releaseParityPiece(stage, spec.releasePieceId);
      revealBolt(stage, spec);
    });
    if (observer) observers.push(observer);
  });

  [...new Set(GAMEPLAY_ART_SPEC.holes.map((hole) => hole.followObjectId).filter(Boolean))]
    .forEach((objectId) => {
      const observer = watchPiece(stage, objectId);
      if (observer) observers.push(observer);
    });

  return () => observers.forEach((observer) => observer.disconnect());
}
